import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { CheckCircle2, Scale, ArrowRight } from "lucide-react";
import {
  BlogNav,
  BlogFooter,
  CTABlock,
  KeyTakeaways,
  FAQSection,
  PostMeta,
  CompetitorDisclaimer,
  trackBlogCTA,
  generateArticleSchema,
  generateFAQSchema
} from "./BlogComponents";

const POST_SLUG = "best-ai-intake-software-for-law-firms-2026";
const POST_DATA = {
  title: "Best AI Intake Software for Law Firms (2026)",
  description: "Compare the top AI-powered intake tools for law firms. Learn how AI scoring helps qualify cases faster and reduce unqualified consultations.",
  author: "HeyIntake Team",
  datePublished: "2026-03-01",
  url: `/blog/${POST_SLUG}`
};

const faqs = [
  {
    question: "What is AI intake software for law firms?",
    answer: "AI intake software automates the client onboarding process by using artificial intelligence to score and qualify potential cases. It analyzes intake form responses to determine case quality, urgency, and fit, helping law firms prioritize high-value matters and reduce time spent on unqualified leads."
  },
  {
    question: "How does AI lead scoring work for legal intake?",
    answer: "AI lead scoring analyzes the information submitted in your intake form — case type, timeline, details provided, and other factors — to generate a quality score (typically 1-10). The AI also provides a plain-English explanation of why each lead scored the way it did, so you can quickly understand the reasoning."
  },
  {
    question: "Is HeyIntake HIPAA compliant?",
    answer: "HeyIntake is not positioned as a HIPAA-first platform. If your law firm handles cases involving protected health information (PHI) and requires strict HIPAA compliance, we recommend evaluating purpose-built compliance tools designed specifically for healthcare-adjacent legal workflows."
  },
  {
    question: "Can AI intake replace my law firm's intake staff?",
    answer: "AI intake software is designed to augment your intake process, not replace your team. It handles the initial scoring and prioritization automatically, so your staff can focus on the most promising leads rather than manually reviewing every submission. Most firms find it increases capacity without adding headcount."
  },
  {
    question: "How long does it take to set up AI intake software?",
    answer: "With HeyIntake, you can create an intake form and start receiving AI-scored submissions within minutes. There's no complex integration required — just build your form, embed it on your website or share the link, and the AI handles scoring automatically."
  },
  {
    question: "Can I customize the AI scoring criteria for my practice areas?",
    answer: "Yes. HeyIntake's AI analyzes the context of your intake form to understand your practice focus. The form questions you include help the AI understand what makes a good case for your firm, and scoring reflects those priorities."
  }
];

export default function LawFirmAIIntakeBlogPost() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  return (
    <>
      <Helmet>
        <title>{POST_DATA.title} | HeyIntake Blog</title>
        <meta name="description" content={POST_DATA.description} />
        <link rel="canonical" href={`https://heyintake.com${POST_DATA.url}`} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateArticleSchema(POST_DATA))
        }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema(faqs))
        }} />
      </Helmet>

      <div className="min-h-screen bg-[#f5f5f0]">
        <BlogNav />

        <article className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <motion.div {...fadeIn}>
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                <Link to="/blog" className="hover:text-foreground">Blog</Link>
                <span>/</span>
                <span>Law Firms</span>
              </div>

              {/* Header */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                {POST_DATA.title}
              </h1>
              
              <PostMeta author={POST_DATA.author} date="March 1, 2026" readTime={8} />

              {/* Key Takeaways */}
              <KeyTakeaways items={[
                "AI intake software scores and prioritizes potential cases automatically",
                "Lead scoring evaluates initial inquiries; intake scoring evaluates detailed submissions",
                "The best tools provide score explanations, not just numbers",
                "HeyIntake is built specifically for AI-driven prioritization and follow-up",
                "Setup takes minutes, not weeks — no technical expertise required"
              ]} />

              {/* Intro */}
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Every law firm faces the same challenge: too many inquiries, not enough qualified cases. Partners spend hours reviewing intake forms that turn out to be poor fits, while promising matters get lost in the shuffle.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  AI intake software changes this equation. Instead of manually triaging every submission, you get instant scoring that tells you which leads deserve attention — and which don't.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  In this guide, we'll break down what AI intake actually means, what to look for when evaluating options, and which tools are best suited for law firm workflows in 2026.
                </p>
              </div>

              {/* Top CTA */}
              <CTABlock postSlug={POST_SLUG} location="top" variant="compact" />

              {/* Section: What AI Intake Means */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                What "AI intake" actually means
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed">
                  There are two distinct types of AI scoring that matter for law firms:
                </p>
                
                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Lead Scoring</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Lead scoring evaluates the initial inquiry. When someone fills out a contact form or requests a consultation, the AI analyzes their responses to predict case quality. It looks at factors like: case type alignment with your practice areas, urgency signals, and how much detail they've provided.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Intake Scoring</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Intake scoring goes deeper. Once a potential client has provided detailed case information — timeline, parties involved, documentation, specific circumstances — the AI evaluates whether this is a matter worth pursuing. It's the difference between "this person might be a good lead" and "this case has strong merit."
                </p>

                <p className="text-muted-foreground leading-relaxed mt-4">
                  The best AI intake software does both: scoring leads as they come in and then re-scoring once you've collected comprehensive intake information.
                </p>
              </div>

              {/* Section: Evaluation Checklist */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                Evaluation checklist for law firms
              </h2>
              <div className="bg-white rounded-xl p-6 border shadow-sm my-6">
                <p className="text-muted-foreground mb-4">When evaluating AI intake software, consider these factors:</p>
                <ul className="space-y-3">
                  {[
                    "Case type routing — Can you direct different inquiries to different forms or workflows?",
                    "Urgency detection — Does the AI identify time-sensitive matters?",
                    "Score explanations — Do you get reasons behind scores, not just numbers?",
                    "Document collection — Can clients upload files or link to external storage?",
                    "Follow-up workflows — Can you email clients directly from the tool?",
                    "Team access — Can multiple attorneys and staff view submissions?",
                    "Pipeline visibility — Can you track matters through stages?",
                    "Audit trail — Is there a record of actions taken on each submission?"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Section: Top Options */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                Top AI intake options for law firms
              </h2>
              
              <div className="space-y-6">
                {/* HeyIntake */}
                <div className="bg-primary/5 border-2 border-primary rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Scale className="w-6 h-6 text-primary" />
                    <h3 className="text-xl font-bold text-foreground">HeyIntake — Best for AI Scoring & Prioritization</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    HeyIntake is purpose-built for service businesses that need to qualify leads fast. It's particularly strong for law firms because of its dual AI scoring approach: lead scoring on initial contact and intake scoring on detailed submissions.
                  </p>
                  <p className="text-muted-foreground mb-4">
                    <strong>Why it stands out:</strong> Every submission gets a score plus a plain-English explanation. You can see your entire pipeline at a glance, email clients directly from the app, and include scheduling links in follow-ups. Setup takes minutes.
                  </p>
                  <Link to="/intake-form-for-lawyers" className="text-primary font-medium hover:underline">
                    Learn more about HeyIntake for law firms →
                  </Link>
                </div>

                {/* IntakeQ */}
                <div className="bg-white rounded-xl p-6 border">
                  <h3 className="text-xl font-semibold text-foreground mb-3">IntakeQ — Healthcare & Clinic-Style Workflows</h3>
                  <p className="text-muted-foreground">
                    IntakeQ is commonly positioned toward healthcare and clinic-style intake workflows. It includes scheduling features and is popular with practices that need appointment-driven intake processes.
                  </p>
                  <CompetitorDisclaimer />
                </div>

                {/* Jotform */}
                <div className="bg-white rounded-xl p-6 border">
                  <h3 className="text-xl font-semibold text-foreground mb-3">Jotform — General-Purpose Form Builder</h3>
                  <p className="text-muted-foreground">
                    Jotform is a widely-used form builder with thousands of templates across industries. It's flexible and can be adapted for legal intake, though AI scoring would require third-party integrations.
                  </p>
                  <CompetitorDisclaimer />
                </div>
              </div>

              <p className="text-sm text-muted-foreground mt-4">
                <Link to="/compare/heyintake-vs-intakeq-vs-jotform" className="text-primary hover:underline">
                  See our full comparison of HeyIntake vs IntakeQ vs Jotform →
                </Link>
              </p>

              {/* Mid CTA */}
              <CTABlock postSlug={POST_SLUG} location="mid" />

              {/* Section: Why HeyIntake */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                Why HeyIntake is best for modern law firm intake
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed">
                  HeyIntake was designed specifically for service businesses that need to qualify and prioritize leads efficiently. For law firms, this translates to several key advantages:
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">AI Lead + Intake Scoring</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Every submission gets scored automatically. Initial inquiries get lead scores; detailed intakes get intake scores. You always know which matters deserve immediate attention.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Score Explanations</h3>
                <p className="text-muted-foreground leading-relaxed">
                  A score without context is just a number. HeyIntake provides plain-English explanations for every score, so you understand why a lead was rated high or low. This helps you make faster decisions with confidence.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Visual Pipeline</h3>
                <p className="text-muted-foreground leading-relaxed">
                  See all your leads and cases in one place. Drag and drop through stages — from new inquiry to consultation scheduled to retained. No spreadsheets or external CRM required.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Built-In Follow-Up</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Email clients directly from HeyIntake. Use templates for common responses. Include your scheduling link so qualified leads can book consultations immediately.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">File Collection</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Add upload links to Google Drive or Dropbox in your intake forms. Clients can share documents, photos, and other evidence before the first consultation.
                </p>
              </div>

              {/* FAQ Section */}
              <FAQSection faqs={faqs} postSlug={POST_SLUG} />

              {/* Footer CTA */}
              <CTABlock postSlug={POST_SLUG} location="footer" />

              {/* Related Links */}
              <div className="mt-12 pt-8 border-t">
                <h3 className="text-lg font-semibold text-foreground mb-4">Related Resources</h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/intake-form-for-lawyers" className="text-primary hover:underline">
                      HeyIntake for Law Firms →
                    </Link>
                  </li>
                  <li>
                    <Link to="/compare/heyintake-vs-intakeq-vs-jotform" className="text-primary hover:underline">
                      Compare HeyIntake vs IntakeQ vs Jotform →
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog/how-to-automate-client-screening-with-ai" className="text-primary hover:underline">
                      How to Automate Client Screening with AI →
                    </Link>
                  </li>
                  <li>
                    <Link to="/pricing" className="text-primary hover:underline">
                      View Pricing →
                    </Link>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </article>

        <BlogFooter />
      </div>
    </>
  );
}
